import React, { useState } from 'react';
import { api } from '../lib/api';
import { addDays, fmtDay, money, today } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Cost, Payment, Task } from '../lib/types';
import { TaskRow, type Ctx } from '../main';

export interface Day {
  date: string; tasks: Task[];
  /** settled: what the ledger recorded */
  actualIn: number; actualOut: number;
  /** promised: what open money tasks say is due */
  expectedIn: number; expectedOut: number;
  /** from the first day shown: settled and promised together */
  balance: number;
}

/**
 * Thirty days, from eight days back: what was settled, what open tasks promise,
 * and the running balance of the two. Exported for the test that checks it.
 */
export function buildDays(tasks: Task[], payments: Payment[], costs: Cost[], now = today(), span = 30, back = 8): Day[] {
  let balance = 0;
  return Array.from({ length: span }, (_, i) => {
    const date = addDays(now, i - back);
    const due = tasks.filter((t) => t.dueDate === date);
    const actualIn = payments.filter((p) => p.received_on.slice(0, 10) === date).reduce((a, b) => a + Number(b.amount), 0);
    const actualOut = costs.filter((c) => c.paid_on.slice(0, 10) === date).reduce((a, b) => a + Number(b.amount), 0);
    const expectedIn = due.filter((t) => t.type === 'Income').reduce((a, b) => a + b.amount, 0);
    const expectedOut = due.filter((t) => t.type === 'Cost').reduce((a, b) => a + b.amount, 0);
    balance += actualIn + expectedIn - actualOut - expectedOut;
    return { date, tasks: due, actualIn, actualOut, expectedIn, expectedOut, balance };
  });
}

const HATCH_IN = 'repeating-linear-gradient(45deg,#0F6B5C,#0F6B5C 3px,#8FC4B8 3px,#8FC4B8 6px)';
const HATCH_OUT = 'repeating-linear-gradient(45deg,#AC4034,#AC4034 3px,#E0AAA3 3px,#E0AAA3 6px)';

/** Work and money side by side. Pick a day for what lands on it. */
export function Timeline({ ctx, tasks }: { ctx: Ctx; tasks: Task[] }): React.JSX.Element {
  const now = today();
  const [selected, setSelected] = useState(now);
  // a specialist cannot see the ledger; the timeline still shows their work
  const payments = useResource(() => (ctx.canManage ? api.payments() : Promise.resolve({ payments: [] })), []);
  const costs = useResource(() => (ctx.canManage ? api.costs() : Promise.resolve({ costs: [] })), []);

  const days = buildDays(tasks, payments.data?.payments ?? [], costs.data?.costs ?? [], now);
  const peak = Math.max(...days.map((d) => Math.max(d.actualIn + d.expectedIn, d.actualOut + d.expectedOut)), 1);
  const day = days.find((d) => d.date === selected) ?? days[8]!;
  const h = (v: number): number => (v ? Math.max((v / peak) * 56, 3) : 0);

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="hd">
          <h3>{fmtDay(days[0]!.date)} → {fmtDay(days[days.length - 1]!.date)}</h3>
          <span className="r sub">{ctx.canManage ? 'Solid = settled · hatched = expected' : 'Your tasks by day'}</span>
        </div>
        <div className="bd">
          <div className="tl">
            {days.map((d) => (
              <button key={d.date} className={`tlday ${d.date === now ? 'today' : ''} ${d.date === selected ? 'sel' : ''}`}
                onClick={() => setSelected(d.date)} aria-label={fmtDay(d.date)}>
                <span className="d">{new Date(`${d.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'narrow' })}</span>
                <span className="n">{new Date(`${d.date}T00:00:00`).getDate()}</span>
                <span className="bars">
                  <span className="col">
                    {d.actualIn > 0 && <i style={{ height: h(d.actualIn), background: '#0F6B5C' }} />}
                    {d.expectedIn > 0 && <i style={{ height: h(d.expectedIn), background: HATCH_IN }} />}
                  </span>
                  <span className="col">
                    {d.actualOut > 0 && <i style={{ height: h(d.actualOut), background: '#AC4034' }} />}
                    {d.expectedOut > 0 && <i style={{ height: h(d.expectedOut), background: HATCH_OUT }} />}
                  </span>
                </span>
                <span className="c">{d.tasks.length || '·'}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid dash2r">
        {ctx.canManage && (
          <div className="card">
            <div className="hd"><h3>{fmtDay(day.date)}{day.date === now ? ' — today' : ''}</h3></div>
            <div className="bd">
              <dl className="kv">
                <dt>In, settled</dt><dd className="mono" style={{ color: 'var(--teal)' }}>{money(day.actualIn)}</dd>
                <dt>In, expected</dt><dd className="mono">{money(day.expectedIn)}</dd>
                <dt>Out, settled</dt><dd className="mono" style={{ color: 'var(--coral)' }}>{money(day.actualOut)}</dd>
                <dt>Out, expected</dt><dd className="mono">{money(day.expectedOut)}</dd>
                <dt>Day net</dt><dd className="mono"><b>{money(day.actualIn + day.expectedIn - day.actualOut - day.expectedOut)}</b></dd>
                <dt>Running balance</dt>
                <dd className="mono"><b style={{ color: day.balance >= 0 ? 'var(--teal)' : 'var(--coral)' }}>{money(day.balance)}</b></dd>
              </dl>
              <div className="note" style={{ marginTop: 14 }}>
                The balance runs from {fmtDay(days[0]!.date)} and mixes money already settled with what open
                tasks promise. An overdue task stays on its original day until it is closed or re-dated.
              </div>
            </div>
          </div>
        )}
        <div className="card">
          <div className="hd"><h3>Due on this day</h3><span className="r"><span className="tag t-mute">{day.tasks.length}</span></span></div>
          <div className="bd">
            {day.tasks.map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
            {day.tasks.length === 0 && <div className="note ok">Nothing due on {fmtDay(day.date)}.</div>}
          </div>
        </div>
      </div>
    </>
  );
}
